/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE SET VERDICT — one written set against the log at its place, and a day's copy against its record (W82 step 3).
 * PURE; `setVerdict.test.ts` and `reviewSheet.test.ts` run it. ONE home for the rule both screens ask:
 *   • the review's table (`reviewSheet.ts`) draws every set's state;
 *   • the queue (`reviewQueue.ts`) counts the SHORT ones, so its `as written` is a claim about the numbers, not only the
 *     count of sets (Fable's ruling, 2026-09-26: `6 of 6 · as written` stood beside a set two reps short).
 *
 *   • PAIRING — `training.dayView`: the plan flattened by section then exercise position, the record by position, the SAME
 *     library id at every place, or no pairing at all (never partial).
 *   • WORKING — `progressHistory.isWorking` on the log; a written set typed `warm_up` is a RAMP, counted nowhere.
 *   • A HOLD by seconds; a WEIGHTED row (a written load above zero) by the load, then the reps; a BODYWEIGHT row by the reps
 *     alone. A measure the athlete did not record claims nothing — the set reads `equal`, never short.
 */

import { dayView, type PlannedExercise, type PerformedExercise } from "./training";
import { isWorking } from "./progressHistory";

export type PlanSet = {
  readonly position: number | null;
  readonly set_type: string | null;
  readonly reps: number | null;
  readonly weight_kg: number | null;
  readonly time_seconds: number | null;
  /** W136 B2 S3 — `coach_workout_sets.distance_m` (metres) / `rounds`; absent = not prescribed. */
  readonly distance_m?: number | null;
  readonly rounds?: number | null;
};

export type PlanExercise = {
  /** The copy's `coach_workout_exercises.id` (W82 step 4: next week's cells are keyed on it); absent on older fixtures. */
  readonly id?: string;
  readonly position: number | null;
  readonly exercise_id: string | null;
  readonly superset_with_next: boolean | null;
  /** The BALANCE clock — the copy's `hold_until_lost`: its sets are holds by KIND, so the sheet reads the logged seconds
   *  for them whatever the written set carries (`reviewSheet`). Absent on older fixtures. */
  readonly hold_until_lost?: boolean | null;
  /** F15 4.1b — the copy's `hidden`: a row a rule tab PAUSED, never shown to or logged by the client, so it is left out of
   *  the day `flatPlan` pairs. Absent = shown (a read without the column, an older fixture). */
  readonly hidden?: boolean | null;
  readonly sets: readonly PlanSet[];
};

export type PlanSection = {
  readonly position: number | null;
  readonly name: string | null;
  readonly section_type: string | null;
  readonly exercises: readonly PlanExercise[];
};

export type VerdictLog = {
  readonly set_number: number | null;
  readonly status: string;
  readonly reps_completed: number | null;
  readonly weight_kg: number | null;
  readonly hold_seconds: number | null;
  /** W136 B2 S3 — `set_logs.distance_m` / `rounds`; absent = not recorded. */
  readonly distance_m?: number | null;
  readonly rounds?: number | null;
};

export type VerdictExercise = { readonly position: number | null; readonly key: string | null; readonly logs: readonly VerdictLog[] };

/** The five states a WRITTEN working set can end in (a ramp is not one of them — it is counted nowhere). */
export type Verdict = "equal" | "short" | "beaten" | "notLogged" | "skipped";

export const isRampSet = (s: PlanSet) => s.set_type === "warm_up";
export const isFoldedSection = (t: string | null) => t === "warm_up" || t === "cool_down";
/** W136 B2 S3 — a written set carrying a distance or rounds is a RUN or a ROUNDS set (D7): never a hold, never reps. */
export const isTyped = (s: PlanSet) => (s.distance_m ?? null) !== null || (s.rounds ?? null) !== null;

/** ONE written set against ONE log — see the header. */
export function classify(written: PlanSet, log: VerdictLog | undefined): Verdict {
  if (log === undefined) return "notLogged";
  if (log.status === "skipped") return "skipped";
  if (!isWorking(log.status)) return "notLogged";
  const cmp = (done: number | null, want: number | null): Verdict | null => {
    if (done === null || want === null || done === want) return null;
    return done > want ? "beaten" : "short";
  };
  // W136 B2 S3 — a RUN / ROUNDS set by its own measures: the load (a carry), the distance, the rounds, the reps beside the
  // rounds, then the time — where LESS is better. Never reps-vs-null, never a hold's seconds (`CoachDashboardFacts.classify`).
  if (isTyped(written)) {
    const load = (written.weight_kg ?? 0) > 0 ? cmp(log.weight_kg, written.weight_kg) : null;
    const t = cmp(log.hold_seconds, written.time_seconds);
    const faster: Verdict | null = t === null ? null : t === "beaten" ? "short" : "beaten";
    return load ?? cmp(log.distance_m ?? null, written.distance_m ?? null) ?? cmp(log.rounds ?? null, written.rounds ?? null)
      ?? ((written.rounds ?? null) !== null ? cmp(log.reps_completed, written.reps) : null) ?? faster ?? "equal";
  }
  if (written.time_seconds !== null) return cmp(log.hold_seconds, written.time_seconds) ?? "equal";
  if (written.weight_kg !== null && written.weight_kg > 0) {
    return cmp(log.weight_kg, written.weight_kg) ?? cmp(log.reps_completed, written.reps) ?? "equal";
  }
  return cmp(log.reps_completed, written.reps) ?? "equal";
}

export type FlatPlanRow = { readonly section: PlanSection; readonly sectionIndex: number; readonly exercise: PlanExercise };

/** The plan in the copy's order: sections by position, exercises by position within each. */
export function flatPlan(sections: readonly PlanSection[]): FlatPlanRow[] {
  const out: FlatPlanRow[] = [];
  [...sections].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).forEach((section, sectionIndex) => {
    for (const exercise of [...section.exercises].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
      // F15 4.1b — a PAUSED row was never shown to the client: it is not part of the day the record is paired with.
      if (exercise.hidden === true) continue;
      out.push({ section, sectionIndex, exercise });
    }
  });
  return out;
}

/** The written sets in their order. */
export const sortedSets = (sets: readonly PlanSet[]) => [...sets].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/** A record's logs by set number (the first row for a number wins). */
export function logsByNumber<L extends VerdictLog>(logs: readonly L[]): Map<number, L> {
  const m = new Map<number, L>();
  for (const l of logs) if (l.set_number !== null && !m.has(l.set_number)) m.set(l.set_number, l);
  return m;
}

/**
 * The record's exercise at each flattened plan row — or NULL when the two do not line up (`training.dayView`: the same
 * library id at every place, or no pairing at all).
 */
export function pairWithPlan<E extends VerdictExercise>(flat: readonly FlatPlanRow[], exercises: readonly E[]): (E | null)[] | null {
  const performed = [...exercises].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const view = dayView(
    flat.map((f, i): PlannedExercise => ({ ordinal: i + 1, libraryExerciseId: f.exercise.exercise_id, name: null, sectionName: f.section.name, sets: [], note: null })),
    performed.map((e, i): PerformedExercise => ({ ordinal: i + 1, libraryExerciseId: e.key, loggedSets: [] })),
  );
  if (view.kind !== "paired") return null;
  const out: (E | null)[] = flat.map(() => null);
  for (const r of view.rows) out[r.planned.ordinal - 1] = r.performed === null ? null : (performed[r.performed.ordinal - 1] ?? null);
  return out;
}

/**
 * How many WRITTEN working sets of the day's main sections ended SHORT against the record — the fact the queue's
 * `as written` needs. NULL when the record does not line up with the plan (nothing can be said about any set's numbers).
 */
export function shortSets(sections: readonly PlanSection[], exercises: readonly VerdictExercise[]): number | null {
  const flat = flatPlan(sections);
  const paired = pairWithPlan(flat, exercises);
  if (paired === null) return null;
  let n = 0;
  flat.forEach((f, i) => {
    if (isFoldedSection(f.section.section_type)) return;
    const logs = logsByNumber(paired[i]?.logs ?? []);
    sortedSets(f.exercise.sets).forEach((s, k) => {
      if (!isRampSet(s) && classify(s, logs.get(k + 1)) === "short") n += 1;
    });
  });
  return n;
}

/**
 * THE WRITTEN MAIN-SECTION EXERCISES THAT LEFT A WORKING SET WITH NO LOG — `not logged`, the record's silence (W82 step 4:
 * the two-week watch read `skipped` rows only, so his Pallof Press — 1 of 2 logged, then 0 of 2 — was invisible to it).
 * ⚠️ It says nothing about whether the set was DONE: a set performed and never recorded reads the same. Library ids, in the
 * copy's order, each once. NULL when the record does not line up with the plan.
 */
export function unloggedExercises(sections: readonly PlanSection[], exercises: readonly VerdictExercise[]): string[] | null {
  const flat = flatPlan(sections);
  const paired = pairWithPlan(flat, exercises);
  if (paired === null) return null;
  const out: string[] = [];
  flat.forEach((f, i) => {
    if (isFoldedSection(f.section.section_type) || f.exercise.exercise_id === null || out.includes(f.exercise.exercise_id)) return;
    const logs = logsByNumber(paired[i]?.logs ?? []);
    if (sortedSets(f.exercise.sets).some((s, k) => !isRampSet(s) && classify(s, logs.get(k + 1)) === "notLogged")) out.push(f.exercise.exercise_id);
  });
  return out;
}

/* ── the copy as the two hooks read it ──────────────────────────────────────────────────────────── */

/** The assigned copy's tree, as ONE select string — the queue (`useReviewQueue`) and the review (`useReviewSheet`) read the
 *  same columns, so the queue's `as written` and the review's table are judged on the same numbers. */
export const PLAN_TREE_SELECT =
  "coach_workout_sections(position, name, section_type, coach_workout_exercises(id, position, exercise_id, superset_with_next, hold_until_lost, hidden, coach_workout_sets(position, set_type, reps, weight_kg, time_seconds, rounds, distance_m)))";

export type PlanTreeSectionRow = {
  position: number | null; name: string | null; section_type: string | null;
  coach_workout_exercises: {
    id?: string; position: number | null; exercise_id: string | null; superset_with_next: boolean | null; hold_until_lost?: boolean | null;
    hidden?: boolean | null;
    coach_workout_sets: { position: number | null; set_type: string | null; reps: number | null; weight_kg: number | string | null; time_seconds: number | null;
      rounds?: number | null; distance_m?: number | string | null }[] | null;
  }[] | null;
};

const num = (v: number | string | null): number | null => {
  if (v === null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** PostgREST's nested rows → the plan (a numeric column may arrive as a string; an absent embed is empty). */
export function planSections(rows: readonly PlanTreeSectionRow[] | null): PlanSection[] {
  return (rows ?? []).map((s) => ({
    position: s.position, name: s.name, section_type: s.section_type,
    exercises: (s.coach_workout_exercises ?? []).map((e) => ({
      ...(e.id !== undefined ? { id: e.id } : {}), position: e.position, exercise_id: e.exercise_id, superset_with_next: e.superset_with_next,
      hold_until_lost: e.hold_until_lost ?? null,
      ...(e.hidden === true ? { hidden: true } : {}),
      sets: (e.coach_workout_sets ?? []).map((x) => ({ position: x.position, set_type: x.set_type, reps: x.reps, weight_kg: num(x.weight_kg), time_seconds: x.time_seconds,
        rounds: x.rounds ?? null, distance_m: num(x.distance_m ?? null) })),
    })),
  }));
}
